/**
 * Color utility functions for normalizing and validating colors
 * Used to ensure Material-UI color functions receive valid color formats
 */

/**
 * Named color mappings to Material Design colors
 */
const NAMED_COLORS: Record<string, string> = {
  red: '#f44336',
  blue: '#2196f3',
  green: '#4caf50',
  yellow: '#ffeb3b',
  orange: '#ff9800',
  purple: '#9c27b0',
  pink: '#e91e63',
  cyan: '#00bcd4',
  teal: '#009688',
  lime: '#cddc39',
  indigo: '#3f51b5',
  brown: '#795548',
  grey: '#9e9e9e',
  gray: '#9e9e9e',
  black: '#000000',
  white: '#ffffff',
};

/**
 * Validates if a color string is in a format supported by Material-UI
 * @param color - Color string to validate
 * @returns true if the color is valid, false otherwise
 */
export const isValidColor = (color: string): boolean => {
  if (!color) return false;

  // Check if it's a valid hex color
  if (/^#([0-9A-F]{3}){1,2}$/i.test(color)) return true;

  // Check if it's a valid rgb/rgba color
  if (/^rgba?\(/.test(color)) return true;

  // Check if it's a valid hsl/hsla color
  if (/^hsla?\(/.test(color)) return true;

  // Check if it's a valid color() function
  if (/^color\(/.test(color)) return true;

  return false;
};

/**
 * Normalizes a color value to ensure it's compatible with Material-UI
 * Converts named colors to hex values and validates format
 * @param color - Color string to normalize (can be undefined)
 * @param fallback - Fallback color to use if normalization fails
 * @returns A valid color string that Material-UI can process
 */
export const normalizeColor = (color: string | undefined, fallback: string): string => {
  if (!color) return fallback;

  // If it's already a valid color format, return as-is
  if (isValidColor(color)) return color;

  // Try to convert named color to hex
  const normalizedColorName = color.toLowerCase().trim();
  if (NAMED_COLORS[normalizedColorName]) {
    return NAMED_COLORS[normalizedColorName];
  }

  // If we can't recognize or normalize the color, return the fallback
  console.warn(`Invalid color format: "${color}". Using fallback: "${fallback}"`);
  return fallback;
};

/**
 * Safely creates an alpha version of a color
 * Normalizes the color first to ensure Material-UI compatibility
 * @param color - Base color (can be undefined)
 * @param alpha - Alpha value (0-1)
 * @param fallback - Fallback color if normalization fails
 * @param alphaFunction - Material-UI alpha function
 * @returns Alpha color string
 */
export const safeAlpha = (
  color: string | undefined,
  alphaValue: number,
  fallback: string,
  alphaFunction: (color: string, alpha: number) => string
): string => {
  const normalizedColor = normalizeColor(color, fallback);
  return alphaFunction(normalizedColor, alphaValue);
};

/**
 * Gets a contrasting text color (black or white) for a given background color
 * @param backgroundColor - Background color to analyze
 * @returns '#000000' for light backgrounds, '#ffffff' for dark backgrounds
 */
export const getContrastingTextColor = (backgroundColor: string): string => {
  const normalizedColor = normalizeColor(backgroundColor, '#ffffff');

  // Convert hex to RGB for luminance calculation
  let r: number, g: number, b: number;

  if (normalizedColor.startsWith('#')) {
    const hex = normalizedColor.slice(1);
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else {
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    }
  } else {
    // For non-hex colors, default to white text
    return '#ffffff';
  }

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black text for light backgrounds, white text for dark backgrounds
  return luminance > 0.5 ? '#000000' : '#ffffff';
};
